import { DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import { useRef, useState, type ReactNode } from "react";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { selectUploadFiles } from "./document-utils";

interface DocumentDropPaneProps {
  maxFiles: number;
  maxSize: number;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  /** Whether anything is listed below the drop area, which changes the pane's shape. */
  hasContents: boolean;
  /** What has been sent, listed under the drop area in the same pane. */
  children?: ReactNode;
}

const megabytes = (bytes: number) => Math.round(bytes / (1024 * 1024));

// The whole pane is the drop target, rather than a dashed rectangle sitting inside a card
// that holds the list. One surface: the top half invites a file, the bottom half is what
// has landed there. Dragging anywhere over it counts, which is what people try anyway.
export const DocumentDropPane = ({
  maxFiles,
  maxSize,
  disabled = false,
  onFiles,
  hasContents,
  children,
}: DocumentDropPaneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [tooBig, setTooBig] = useState<string[]>([]);
  const [tooMany, setTooMany] = useState<string[]>([]);

  const accept = (files: File[]) => {
    if (disabled || !files.length) return;

    const selection = selectUploadFiles(files, maxFiles, maxSize);
    setTooBig(selection.oversized.map((file) => file.name));
    setTooMany(selection.excess.map((file) => file.name));

    if (selection.accepted.length) onFiles(selection.accepted);
  };

  return (
    <Card
      className={cn(
        "w-full border-stroke-tertiary shadow-sm transition-colors",
        { "opacity-70": disabled },
      )}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        event.preventDefault();
        setIsDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragging(false);
        accept(Array.from(event.dataTransfer.files));
      }}
    >
      {/* The dashed outline is what says "drop here": a tinted well inside the pane, so it
          reads as the target rather than a second box competing with the pane. */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "m-4 rounded-lg border-2 border-dashed px-6 py-8 transition-colors",
          {
            "border-primary bg-primary/5": isDragging,
            "border-stroke-secondary bg-surface-secondary": !isDragging,
            "hover:border-stroke-primary": !disabled && !isDragging,
            "cursor-not-allowed": disabled,
          },
        )}
        style={{ width: "calc(100% - 2rem)" }}
      >
        <Stack gap="xs" align="center" fullWidth>
          <DocumentArrowUpIcon className="h-8 w-8 text-tertiary-text" />
          <Typography variant="subtitleW3">
            Drop documents here, or click to choose them
          </Typography>
          <Typography variant="captionW1" textColor="tertiary">
            Any file type. Up to {maxFiles} at a time, {megabytes(maxSize)} MB
            each.
          </Typography>
        </Stack>
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="sr-only"
        onChange={(event) => {
          accept(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />

      {tooBig.length > 0 && (
        <Stack className="px-4 pb-3" fullWidth>
          <Typography variant="captionW1" className="text-destructive">
            {tooBig.join(", ")} {tooBig.length === 1 ? "is" : "are"} over{" "}
            {megabytes(maxSize)} MB, so {tooBig.length === 1 ? "it" : "they"}{" "}
            {tooBig.length === 1 ? "was" : "were"} not sent.
          </Typography>
        </Stack>
      )}

      {tooMany.length > 0 && (
        <Stack className="px-4 pb-3" fullWidth>
          <Typography variant="captionW1" className="text-destructive">
            {tooMany.join(", ")} {tooMany.length === 1 ? "was" : "were"} not
            sent. You can send up to {maxFiles} files at a time.
          </Typography>
        </Stack>
      )}

      {hasContents && (
        <Stack
          className="border-t border-stroke-tertiary px-4 py-1"
          align="stretch"
          fullWidth
        >
          {children}
        </Stack>
      )}
    </Card>
  );
};
